import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import CreateOrderModal from "./CreateOrderModal.js";
import api from "../../services/api.js";
import CloseServiceModal from "./CloseServiceModal.js";
import UpdateServiceModal from "./UpdateServiceModal.js";

export default function CustomerServiceView({ role, cust_id, token }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openOrder, setOpenOrder] = useState(false);
  const [openCloseModal, setOpenCloseModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  const [selectedService, setSelectedService] = useState(null);

  const inactiveStatuses = [
    "Terminated",
    "Closed",
    "Not Active",
    "Disable",
    "-",
    "",
  ];

  const formatDate = (dateString) => {
    if (!dateString || dateString === "No order yet") return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchService = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/subscribe/${cust_id}`, {
        headers: { Authorization: token },
      });

      const data = res.data?.data;

      if (Array.isArray(data)) {
        setServices(data);
      } else if (data && typeof data === "object") {
        setServices([data]);
      } else {
        setServices([]);
      }
    } catch (err) {
      console.error("Error fetching service:", err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [cust_id, token]);

  useEffect(() => {
    if (cust_id && token) fetchService();
  }, [fetchService, cust_id, token]);

  return (
    <>
      {/* HEADER */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
        <h2 className="text-xl font-bold dark:text-white">
          Service Information
        </h2>
      </div>

      {/* ACTION BAR */}
      <div className="flex flex-wrap gap-3 mb-4">
        {role === "billing" && (
          <button
            onClick={() => setOpenOrder(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 ml-auto rounded shadow"
          >
            Create Order
          </button>
        )}
      </div>

      <CreateOrderModal
        open={openOrder}
        onClose={() => setOpenOrder(false)}
        cust_id={cust_id}
        token={token}
        onSuccess={fetchService}
      />

      {/* CONTENT */}
      {loading ? (
        <p className="text-gray-500">Loading service information...</p>
      ) : services.length === 0 ? (
        <div className="p-8 border-2 border-dashed rounded-lg text-center">
          <p className="text-red-500">No service found for this customer.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {services.map((service) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow title="Service Name" value={service.name} />
                <InfoRow title="Service ID" value={service.id} />
                <InfoRow
                  title="First Payment"
                  value={service.firstpaymentamount}
                />
                <InfoRow title="Recurring" value={service.recurringamount} />
                <InfoRow title="Billing Cycle" value={service.billingcycle} />
                <InfoRow
                  title="Registered At"
                  value={formatDate(service.regdate)}
                />
                <InfoRow
                  title="Next Due Date"
                  value={formatDate(service.nextduedate)}
                  isWarning={
                    service.nextduedate &&
                    new Date(service.nextduedate) < new Date()
                  }
                />
                <InfoRow title="Status" value={service.status} />
                <InfoRow title="Note" value={service.notes} />
              </div>

              {!inactiveStatuses.includes(service.status) &&
                role === "billing" && (
                  <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                    <button
                      onClick={() => {
                        setSelectedService(service);
                        setOpenUpdateModal(true);
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      Update Service
                    </button>

                    <button
                      onClick={() => {
                        setSelectedService(service);
                        setOpenCloseModal(true);
                      }}
                      className="bg-red-600 text-white px-4 py-2 rounded"
                    >
                      Terminate Service
                    </button>
                  </div>
                )}
            </motion.div>
          ))}
        </div>
      )}

      {/* MODALS */}
      <UpdateServiceModal
        open={openUpdateModal}
        onClose={() => setOpenUpdateModal(false)}
        service={selectedService}
        token={token}
        onSuccess={fetchService}
      />

      <CloseServiceModal
        open={openCloseModal}
        onClose={() => setOpenCloseModal(false)}
        serviceId={selectedService?.id}
        token={token}
        onSuccess={fetchService}
      />
    </>
  );
}

// ===============================
// INFO ROW
// ===============================
function InfoRow({ title, value, isWarning }) {
  return (
    <div className="p-4 border rounded-lg">
      <div className="text-xs font-semibold uppercase text-gray-400">
        {title}
      </div>
      <div
        className={`mt-1 text-lg font-semibold ${
          isWarning ? "text-red-500" : ""
        }`}
      >
        {value || "-"}
      </div>
    </div>
  );
}
